import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ThumbsUp, ThumbsDown, TrendingUp, MessageSquare, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface FeedbackStats {
  total: number;
  helpful: number;
  notHelpful: number;
  helpfulPercentage: number;
}

interface MessageWithFeedback {
  id: string;
  content: string;
  created_at: string;
  conversation_id: string;
  feedback: {
    is_helpful: boolean;
    created_at: string;
    user_id: string;
  };
  user_message?: string;
}

interface CommonIssue {
  pattern: string;
  count: number;
  examples: string[];
}

const ChatAnalytics = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<FeedbackStats>({
    total: 0,
    helpful: 0,
    notHelpful: 0,
    helpfulPercentage: 0
  });
  const [recentFeedback, setRecentFeedback] = useState<MessageWithFeedback[]>([]);
  const [commonIssues, setCommonIssues] = useState<CommonIssue[]>([]);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/');
        return;
      }

      // Check if user has admin role
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .single();

      if (error || !roles) {
        toast({
          title: "Access Denied",
          description: "You don't have permission to access this page.",
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      setIsAdmin(true);
      loadAnalytics();
    } catch (error) {
      console.error('Error checking admin access:', error);
      navigate('/');
    }
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);

      // Get feedback statistics
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('chat_feedback')
        .select('is_helpful');

      if (feedbackError) throw feedbackError;

      const total = feedbackData?.length || 0;
      const helpful = feedbackData?.filter(f => f.is_helpful).length || 0;
      const notHelpful = total - helpful;
      const helpfulPercentage = total > 0 ? Math.round((helpful / total) * 100) : 0;

      setStats({ total, helpful, notHelpful, helpfulPercentage });

      // Get recent feedback with message context
      const { data: messagesData, error: messagesError } = await supabase
        .from('chat_messages')
        .select(`
          id,
          content,
          created_at,
          conversation_id,
          chat_feedback (
            is_helpful,
            created_at,
            user_id
          )
        `)
        .eq('role', 'assistant')
        .not('chat_feedback', 'is', null)
        .order('created_at', { ascending: false })
        .limit(50);

      if (messagesError) throw messagesError;

      // Get user messages for context
      const enrichedMessages: MessageWithFeedback[] = [];
      
      for (const msg of messagesData || []) {
        if (!msg.chat_feedback || msg.chat_feedback.length === 0) continue;
        
        // Get the user message that preceded this assistant message
        const { data: userMsg } = await supabase
          .from('chat_messages')
          .select('content')
          .eq('conversation_id', msg.conversation_id)
          .eq('role', 'user')
          .lt('created_at', msg.created_at)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        enrichedMessages.push({
          id: msg.id,
          content: msg.content,
          created_at: msg.created_at,
          conversation_id: msg.conversation_id,
          feedback: msg.chat_feedback[0],
          user_message: userMsg?.content
        });
      }

      setRecentFeedback(enrichedMessages);

      // Analyze common issues from negative feedback
      const negativeMessages = enrichedMessages
        .filter(m => !m.feedback.is_helpful)
        .map(m => m.user_message || '')
        .filter(m => m.length > 0);

      const issues = analyzeCommonIssues(negativeMessages);
      setCommonIssues(issues);

    } catch (error) {
      console.error('Error loading analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const analyzeCommonIssues = (messages: string[]): CommonIssue[] => {
    const patterns = [
      { pattern: 'upgrade', keywords: ['upgrade', 'plan', 'subscription', 'premium', 'first class'] },
      { pattern: 'pricing', keywords: ['price', 'cost', 'payment', 'billing', 'charge'] },
      { pattern: 'packing', keywords: ['pack', 'luggage', 'baggage', 'suitcase', 'checklist'] },
      { pattern: 'currency', keywords: ['currency', 'exchange', 'convert', 'rate'] },
      { pattern: 'cancel', keywords: ['cancel', 'unsubscribe', 'refund', 'stop'] },
      { pattern: 'features', keywords: ['feature', 'how to', 'use', 'work'] },
      { pattern: 'technical', keywords: ['error', 'bug', 'not working', 'broken', 'issue'] },
      { pattern: 'account', keywords: ['account', 'login', 'password', 'profile'] },
    ];

    const issueMap: { [key: string]: { count: number; examples: string[] } } = {};

    messages.forEach(msg => {
      const lowerMsg = msg.toLowerCase();
      patterns.forEach(({ pattern, keywords }) => {
        if (keywords.some(keyword => lowerMsg.includes(keyword))) {
          if (!issueMap[pattern]) {
            issueMap[pattern] = { count: 0, examples: [] };
          }
          issueMap[pattern].count++;
          if (issueMap[pattern].examples.length < 3) {
            issueMap[pattern].examples.push(msg.substring(0, 100));
          }
        }
      });
    });

    return Object.entries(issueMap)
      .map(([pattern, data]) => ({
        pattern,
        count: data.count,
        examples: data.examples
      }))
      .sort((a, b) => b.count - a.count);
  };

  if (!isAdmin || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Chat Analytics - OdyVoy Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/')}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
                <h1 className="text-2xl font-bold">Chat Analytics</h1>
              </div>
              <Button onClick={loadAnalytics} variant="outline" size="sm">
                Refresh Data
              </Button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Feedback
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.total}</div>
                <div className="flex items-center gap-2 mt-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">responses rated</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Helpful
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{stats.helpful}</div>
                <div className="flex items-center gap-2 mt-2">
                  <ThumbsUp className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-muted-foreground">{stats.helpfulPercentage}% positive</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Not Helpful
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">{stats.notHelpful}</div>
                <div className="flex items-center gap-2 mt-2">
                  <ThumbsDown className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-muted-foreground">{100 - stats.helpfulPercentage}% negative</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Success Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.helpfulPercentage}%</div>
                <div className="flex items-center gap-2 mt-2">
                  <TrendingUp className={`h-4 w-4 ${stats.helpfulPercentage >= 70 ? 'text-green-600' : 'text-yellow-600'}`} />
                  <span className="text-sm text-muted-foreground">
                    {stats.helpfulPercentage >= 70 ? 'Good' : 'Needs improvement'}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="issues" className="space-y-4">
            <TabsList>
              <TabsTrigger value="issues">Common Issues</TabsTrigger>
              <TabsTrigger value="feedback">Recent Feedback</TabsTrigger>
            </TabsList>

            <TabsContent value="issues">
              <Card>
                <CardHeader>
                  <CardTitle>Common Issues from Negative Feedback</CardTitle>
                  <CardDescription>
                    Issues identified from user questions that received negative feedback
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {commonIssues.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No common issues identified yet
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {commonIssues.map((issue, idx) => (
                        <div key={idx} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <AlertCircle className="h-5 w-5 text-yellow-600" />
                              <h3 className="font-semibold capitalize">{issue.pattern}</h3>
                            </div>
                            <Badge variant="secondary">{issue.count} occurrences</Badge>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-muted-foreground">Example questions:</p>
                            {issue.examples.map((example, exIdx) => (
                              <p key={exIdx} className="text-sm pl-4 border-l-2 border-muted">
                                "{example}..."
                              </p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="feedback">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Feedback</CardTitle>
                  <CardDescription>
                    Latest AI responses with user feedback
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[600px] pr-4">
                    <div className="space-y-4">
                      {recentFeedback.map((item) => (
                        <div
                          key={item.id}
                          className={`border rounded-lg p-4 ${
                            item.feedback.is_helpful ? 'border-green-200 bg-green-50/50 dark:bg-green-950/20' : 'border-red-200 bg-red-50/50 dark:bg-red-950/20'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {item.feedback.is_helpful ? (
                                <ThumbsUp className="h-4 w-4 text-green-600" />
                              ) : (
                                <ThumbsDown className="h-4 w-4 text-red-600" />
                              )}
                              <Badge variant={item.feedback.is_helpful ? "default" : "destructive"}>
                                {item.feedback.is_helpful ? 'Helpful' : 'Not Helpful'}
                              </Badge>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(item.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          
                          {item.user_message && (
                            <div className="mb-3">
                              <p className="text-xs font-semibold text-muted-foreground mb-1">User Question:</p>
                              <p className="text-sm bg-background/50 p-2 rounded border">
                                {item.user_message}
                              </p>
                            </div>
                          )}
                          
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-1">AI Response:</p>
                            <p className="text-sm bg-background/50 p-2 rounded border">
                              {item.content.length > 300 
                                ? item.content.substring(0, 300) + '...' 
                                : item.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </>
  );
};

export default ChatAnalytics;
